import PropTypes from 'prop-types';
import React from 'react';
import { withRouter } from 'react-router';
import { MuiThemeProvider, createTheme } from '@material-ui/core';
import { theme } from '@ublend-npm/aulaui-next';
import { ChildRouteType } from '@app/components/propTypes/common';

import { isLocalId } from '@core/utils/localId';
import {
  goToEducatorAreaWithBackButton,
  goToMaterialsWithBackButton,
  showBackButtonForContent,
} from '@core/constants/backButtonNavigation';
import {
  getHomePath,
  isAulaLogoLeaveDestination,
  isAulaLogoNavLocation,
  getAulaLogoLeaveDestination,
} from '@app/actions/navigation/routes';
import push from '../../../../utils/routing';
import {
  MATERIAL_PERMISSIONS,
  getPermissionDeniedMessage,
} from '@core/materials/permissions';

import * as types from './types';
import DeleteSectionModal from './MaterialList/DeleteSectionModal.container';
import LeaveWithoutSavingModal from './SectionView/LeaveWithoutSavingModal';
import GetStarted from './GetStarted';
import SectionView from './SectionView.container';
import EmptyPage from './EmptyPage';
import EmptyContent from './EmptyContent/EmptyContent';
import VersionModal from './Versions';
import {
  Container,
  SectionViewContainer,
  ContentContainer,
} from './Material.styled';
import noContent from '../../../../../static/no-content.png';
// TODO move this to a generic location
const { color, font } = theme;
const aulaTheme = createTheme({
  typography: {
    fontFamily: font.family.workSans,
    useNextVariants: true,
  },
  palette: {
    primary: {
      main: color.designSystem.purple70,
    },
    text: {
      hint: color.grey11,
    },
  },
});

class MaterialV2 extends React.PureComponent {
  constructor(props) {
    super(props);

    this.handleOnCloseLeaveWithoutSaving =
      this.handleOnCloseLeaveWithoutSaving.bind(this);
    this.handleOnConfirmLeaveWithoutSaving =
      this.handleOnConfirmLeaveWithoutSaving.bind(this);
  }

  componentWillMount() {
    const { route, router, educatorOnly } = this.props;
    const tab = educatorOnly ? 'educatorArea' : 'materials';
    // We need this fallback for backwards compatibility with old URLs
    const materialRoute = route.childRoutes
      ? route.childRoutes.find((childRoute) => childRoute.path.includes(tab))
      : route;
    this.unregisterLeaveHook = router.setRouteLeaveHook(
      Object.assign(materialRoute, {
        // Must be explicit since path might be '/materials' or '/educatorArea' at mount time
        path: `${tab}(/:sectionId)`,
      }),
      this.routerWillLeave.bind(this)
    );
  }

  componentDidMount() {
    const { currentSectionId, dispatchAnalytics } = this.props;
    this.fallbackWhenNoSectionExists();

    if (!isLocalId(currentSectionId)) {
      dispatchAnalytics();
    }
    this.updateDocumentTitle();
  }

  componentDidUpdate(prevProps) {
    const {
      currentSectionId,
      dispatchAnalytics,
      leaveDestinationPath,
      onLeaveWithoutSaving,
      editing,
      saving,
    } = this.props;
    const shouldDispatchChangeSectionAnalytics =
      prevProps.currentSectionId !== currentSectionId &&
      typeof currentSectionId !== 'undefined' &&
      !isLocalId(currentSectionId);

    if (shouldDispatchChangeSectionAnalytics) {
      dispatchAnalytics();
    }

    if (leaveDestinationPath && !editing && !saving) {
      push(leaveDestinationPath);
      onLeaveWithoutSaving(null);
    }
    this.updateDocumentTitle();
  }

  componentWillUnmount() {
    if (this.unregisterLeaveHook) {
      this.unregisterLeaveHook();
    }
  }

  routerWillLeave(e) {
    const { currentSectionId, editing, onLeaveWithoutSaving } = this.props;
    if (typeof currentSectionId !== 'undefined' && editing) {
      const destination = isAulaLogoNavLocation(e)
        ? getAulaLogoLeaveDestination()
        : e.pathname;
      onLeaveWithoutSaving(destination, null);
      return false;
    }

    return true;
  }

  updateDocumentTitle() {
    const { currentSectionTitle, currentSpaceName } = this.props;
    const section = currentSectionTitle
      ? `${currentSectionTitle} - Materials - `
      : '';
    const space = currentSpaceName ? `${currentSpaceName} - ` : '';
    document.title = `${section}${space}Aula`;
  }

  handleOnCloseLeaveWithoutSaving() {
    const { onLeaveWithoutSaving } = this.props;
    onLeaveWithoutSaving(null, true);
  }

  handleOnConfirmLeaveWithoutSaving() {
    const {
      onLeaveWithoutSaving,
      leaveDestinationPath,
      params,
      fallbackId,
      educatorOnly,
      isBackButtonRouteAllowed,
      editing,
      currentSectionId,
    } = this.props || {};

    // Aula logo only (not back button → home): both can target /home, so logo
    // navigations are marked via location state / leave destination marker.
    if (isAulaLogoLeaveDestination(leaveDestinationPath)) {
      onLeaveWithoutSaving(getHomePath(), false);
      return;
    }

    // TODO: Enable full single-click leave for all destinations (sidebar, back, etc.)
    // Prefer the destination captured by setRouteLeaveHook. Overwriting it with
    // materials+fallback when editing made push a no-op, so leave required a second click.
    // onLeaveWithoutSaving(leaveDestinationPath || null, false);
    // return;

    const { showBackbutton, queryParams } = showBackButtonForContent();
    const isBackButtonEnabled = isBackButtonRouteAllowed && showBackbutton;
    // Check if its a new page or not
    const isNewSection = isLocalId(currentSectionId);
    // Check if we should use back button route
    const shouldUseBackButtonRoute =
      isBackButtonEnabled && (isNewSection || editing);

    let destinationPath = leaveDestinationPath;

    // To remove the last value from 'from' parameter
    let modifiedQueryParams = queryParams;
    if (queryParams && queryParams.from) {
      const fromValues = queryParams.from.split(',');
      if (fromValues.length > 1) {
        // Remove the last value only if there are multiple values
        fromValues.pop();
        modifiedQueryParams = {
          ...queryParams,
          from: fromValues.join(','),
        };
      }
    }

    if (shouldUseBackButtonRoute) {
      destinationPath = educatorOnly
        ? goToEducatorAreaWithBackButton(
            params?.classId,
            fallbackId,
            modifiedQueryParams
          )
        : goToMaterialsWithBackButton(
            params?.classId,
            fallbackId,
            modifiedQueryParams
          );
    }

    // TODO : Remove this later
    // Check if back button is allowed via feature flag
    // const allowBackButton = isBackButtonRouteAllowed && showBackbutton;
    // // Check if its local id (new one) or existing page
    // const isLocal = isLocalId(currentSectionId);

    // // Case 1 — If a new page is created and we clicking back button or browser back
    // if (allowBackButton && isLocal) {
    //   destinationPath = educatorOnly
    //     ? goToEducatorAreaWithBackButton(params?.classId, fallbackId)
    //     : goToMaterialsWithBackButton(params?.classId, fallbackId);

    //   // Case 2 — If we edit a existing page and clicking back button or browser back
    // } else if (allowBackButton && editing) {
    //   destinationPath = educatorOnly
    //     ? goToEducatorAreaWithBackButton(params?.classId, fallbackId)
    //     : goToMaterialsWithBackButton(params?.classId, fallbackId);
    // }

    onLeaveWithoutSaving(destinationPath, false);
  }

  fallbackWhenNoSectionExists() {
    const {
      currentSectionId,
      section,
      hasAtLeastOneChildSection,
      goToFallbackSection,
      fallbackId,
      canUserViewMaterial,
    } = this.props;

    if (
      (!section || !currentSectionId || !canUserViewMaterial) &&
      hasAtLeastOneChildSection &&
      fallbackId
    ) {
      goToFallbackSection();
    }
  }

  render() {
    const {
      hasFetchedSections,
      sectionIds,
      section,
      leaveDestinationPath,
      editing,
      saving,
      isDeletingSection,
      isDeletingFolder,
      isInstructor,
      hasAtLeastOneChildSection,
      currentSectionId,
      educatorOnly,
      canViewMaterials,
      isPermissionDeniedError,
    } = this.props;

    // Show permission denied page if:
    // 1. User doesn't have view permission (from permissions check), OR
    // 2. API returned a 403 permission denied error
    if (
      !canViewMaterials ||
      (!educatorOnly && isPermissionDeniedError && sectionIds.length === 0)
    ) {
      return (
        <MuiThemeProvider theme={aulaTheme}>
          <Container>
            <ContentContainer>
              <SectionViewContainer>
                <EmptyContent
                  image={noContent}
                  copy={getPermissionDeniedMessage(MATERIAL_PERMISSIONS.VIEW)}
                />
              </SectionViewContainer>
            </ContentContainer>
          </Container>
        </MuiThemeProvider>
      );
    }

    const displayGetStarted =
      hasFetchedSections &&
      sectionIds.length === 0 &&
      !hasAtLeastOneChildSection;

    const getSectionViewComponent = () => {
      if (displayGetStarted) {
        return (
          <ContentContainer>
            <SectionViewContainer>
              <GetStarted />
            </SectionViewContainer>
          </ContentContainer>
        );
      }

      if (!section && !hasAtLeastOneChildSection) {
        return (
          <ContentContainer>
            <SectionViewContainer>
              <EmptyPage isInstructor={isInstructor} />
            </SectionViewContainer>
          </ContentContainer>
        );
      }

      if (isDeletingSection || isDeletingFolder) {
        return <div />;
      }

      // Render SectionView if we have a currentSectionId, even if section isn't in store yet
      // SectionView will trigger getSection to fetch the content
      if (currentSectionId) {
        return <SectionView educatorOnly={educatorOnly} />;
      }

      return null;
    };

    const getVersionModalComponent = () => {
      return <VersionModal educatorOnly={educatorOnly} />;
    };

    return (
      <MuiThemeProvider theme={aulaTheme}>
        <Container>
          {getSectionViewComponent()}
          <LeaveWithoutSavingModal
            open={leaveDestinationPath !== null && editing && !saving}
            onClose={this.handleOnCloseLeaveWithoutSaving}
            onConfirm={this.handleOnConfirmLeaveWithoutSaving}
          />
          <DeleteSectionModal
            educatorOnly={educatorOnly}
            sectionTitle={section?.title}
          />
          {getVersionModalComponent()}
        </Container>
      </MuiThemeProvider>
    );
  }
}

MaterialV2.propTypes = {
  dispatchAnalytics: PropTypes.func.isRequired,
  currentSectionId: PropTypes.string, // eslint-disable-line react/require-default-props
  currentSectionTitle: PropTypes.string,
  currentSpaceName: PropTypes.string,
  section: types.Section,
  sectionIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  editing: PropTypes.bool,
  saving: PropTypes.bool,
  hasAtLeastOneChildSection: PropTypes.bool.isRequired,
  hasFetchedSections: PropTypes.bool,
  onLeaveWithoutSaving: PropTypes.func.isRequired,
  leaveDestinationPath: PropTypes.string,
  route: PropTypes.shape({
    childRoutes: PropTypes.arrayOf(ChildRouteType),
    path: PropTypes.string,
  }).isRequired,
  router: PropTypes.shape({
    setRouteLeaveHook: PropTypes.func.isRequired,
  }).isRequired,
  isDeletingSection: PropTypes.bool.isRequired,
  isDeletingFolder: PropTypes.bool.isRequired,
  isInstructor: PropTypes.bool.isRequired,
  educatorOnly: PropTypes.bool,
  goToFallbackSection: PropTypes.func.isRequired,
  fallbackId: PropTypes.string,
  isBackButtonRouteAllowed: PropTypes.bool,
  params: PropTypes.shape({
    classId: PropTypes.string,
  }),
  canViewMaterials: PropTypes.bool,
  isPermissionDeniedError: PropTypes.bool,
  onPermissionDenied: PropTypes.func,
};

MaterialV2.defaultProps = {
  currentSectionTitle: '',
  currentSpaceName: '',
  section: null,
  editing: false,
  saving: false,
  hasFetchedSections: false,
  leaveDestinationPath: null,
  educatorOnly: false,
  fallbackId: null,
  isBackButtonRouteAllowed: false,
  params: null,
  canViewMaterials: true,
  isPermissionDeniedError: false,
  onPermissionDenied: () => {},
};

export default withRouter(MaterialV2);
